import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Home, Car, Package, MapPin, Layers, DollarSign, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import DynamicAssetForm from '../components/DynamicAssetForm';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const Assets = () => {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showUnitModal, setShowUnitModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [assetTypes, setAssetTypes] = useState([]);
    const [expandedAsset, setExpandedAsset] = useState(null);
    const [units, setUnits] = useState([]);
    const [showBulkUnitModal, setShowBulkUnitModal] = useState(false);
    const [bulkUnitData, setBulkUnitData] = useState({ prefix: '', startNumber: 1, count: 1, baseRate: null });
    const [bulkProgress, setBulkProgress] = useState(null); // { total, current, active }

    useEffect(() => {
        fetchAssets();
        fetchAssetTypes();
    }, []);

    const fetchAssets = async () => {
        try {
            const response = await api.get('/assets');
            setAssets(response.data.data.data);
        } catch (error) {
            console.error('Error fetching assets:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssetTypes = async () => {
        try {
            const response = await api.get('/assets/types');
            setAssetTypes(response.data.data);
        } catch (error) {
            console.error('Error fetching asset types:', error);
        }
    };

    const fetchUnits = async (assetId) => {
        try {
            const response = await api.get(`/assets/${assetId}/units`);
            setUnits(response.data.data);
        } catch (error) {
            console.error('Error fetching units:', error);
        }
    };

    // FIX: Added the missing openEditModal function
    const openEditModal = (asset) => {
        setSelectedAsset(asset);
        setShowEditModal(true);
    };

    const handleCreateAsset = async (data) => {
        try {
            const standardFields = ['name', 'category', 'base_rate', 'description', 'billing_cycle'];
            const specifications = {};
            
            Object.keys(data).forEach(key => {
                if (!standardFields.includes(key) && data[key] !== undefined && data[key] !== '') {
                    specifications[key] = data[key];
                }
            });
            
            const assetData = {
                name: data.name,
                category: data.category,
                base_rate: parseFloat(data.base_rate),
                description: data.description || '',
                billing_cycle: data.billing_cycle || 'monthly',
                specifications: specifications
            };
            
            const response = await api.post('/assets', assetData);
            
            // After creating asset, prompt to add units
            if (response.data.success) {
                setSelectedAsset(response.data.data);
                setShowUnitModal(true);
            }
            
            setShowModal(false);
            fetchAssets();
        } catch (error) {
            console.error('Error creating asset:', error);
            alert(error.response?.data?.error || 'Failed to create asset');
        }
    };

    const handleCreateUnit = async (unitData) => {
        try {
            // Ensure all values are properly defined
            const dataToSend = {
                asset_id: selectedAsset?.id || selectedAsset,
                unit_number: unitData.unit_number,
                name: unitData.name || null,
                rate_override: unitData.rate_override ? parseFloat(unitData.rate_override) : null,
                status: unitData.status || 'available'
            };
            
            console.log('Sending unit data:', dataToSend);
            
            await api.post('/assets/units', dataToSend);
            if (expandedAsset === (selectedAsset?.id || selectedAsset)) {
                fetchUnits(selectedAsset?.id || selectedAsset);
            }
            setShowUnitModal(false);
            setSelectedUnit(null);
            setSelectedAsset(null);
            fetchAssets(); // Refresh to update unit counts
        } catch (error) {
            console.error('Error creating unit:', error);
            alert(error.response?.data?.error || 'Failed to create unit');
        }
    };

    const handleBulkCreateUnits = async () => {
        try {
            const total = bulkUnitData.count;
            setBulkProgress({ total, current: 0, active: true });
            
            const startIdx = bulkUnitData.startNumber;
            const prefix = bulkUnitData.prefix;
            const assetId = selectedAsset?.id || selectedAsset;
            
            // Build all units at once
            const units = [];
            for (let i = 0; i < total; i++) {
                const unitNumber = `${prefix}${startIdx + i}`;
                units.push({
                    asset_id: assetId,
                    unit_number: unitNumber,
                    name: `${unitNumber}`,
                    rate_override: bulkUnitData.baseRate || null,
                    status: 'available'
                });
            }
            
            setBulkProgress({ total, current: 1, active: true, message: 'Sending bulk request...' });
            
            // Use the bulk API endpoint (much faster than individual requests)
            const response = await api.post('/assets/units/bulk', { units });
            const created = response.data?.data?.created || 0;
            
            setBulkProgress({ total, current: total, active: false });
            
            if (expandedAsset === assetId) {
                fetchUnits(assetId);
            }
            
            setTimeout(() => {
                setShowBulkUnitModal(false);
                setBulkUnitData({ prefix: '', startNumber: 1, count: 1, baseRate: null });
                setSelectedAsset(null);
                setBulkProgress(null);
                fetchAssets();
            }, 1500);
        } catch (error) {
            console.error('Error creating bulk units:', error);
            setBulkProgress(null);
            alert('Failed to create units: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleUpdateUnit = async (unitId, data) => {
        try {
            await api.put(`/assets/units/${unitId}`, data);
            if (expandedAsset === selectedAsset?.id) {
                fetchUnits(selectedAsset?.id);
            }
            setSelectedUnit(null);
            fetchAssets();
        } catch (error) {
            console.error('Error updating unit:', error);
            alert('Failed to update unit');
        }
    };

    const handleDeleteUnit = async (unitId) => {
        if (window.confirm('Are you sure you want to delete this unit?')) {
            try {
                await api.delete(`/assets/units/${unitId}`);
                if (expandedAsset === selectedAsset?.id) {
                    fetchUnits(selectedAsset?.id);
                }
                fetchAssets();
            } catch (error) {
                console.error('Error deleting unit:', error);
                alert('Failed to delete unit');
            }
        }
    };

    const handleUpdateAsset = async (data) => {
        try {
            const assetData = {
                name: data.name,
                category: data.category,
                base_rate: parseFloat(data.base_rate),
                description: data.description || '',
                billing_cycle: data.billing_cycle || 'monthly',
            };
            
            await api.put(`/assets/${selectedAsset.id}`, assetData);
            setShowEditModal(false);
            setSelectedAsset(null);
            fetchAssets();
        } catch (error) {
            console.error('Error updating asset:', error);
            alert('Failed to update asset');
        }
    };

    const handleDeleteAsset = async (assetId, assetName) => {
        if (window.confirm(`Are you sure you want to delete "${assetName}" and all its units? This action cannot be undone.`)) {
            try {
                await api.delete(`/assets/${assetId}`);
                fetchAssets();
                if (expandedAsset === assetId) {
                    setExpandedAsset(null);
                }
            } catch (error) {
                console.error('Error deleting asset:', error);
                alert('Failed to delete asset');
            }
        }
    };

    const toggleAssetExpand = async (asset) => {
        if (expandedAsset === asset.id) {
            setExpandedAsset(null);
            setUnits([]);
        } else {
            setExpandedAsset(asset.id);
            await fetchUnits(asset.id);
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'property': return <Home className="h-5 w-5" />;
            case 'vehicle': return <Car className="h-5 w-5" />;
            default: return <Package className="h-5 w-5" />;
        }
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
                    <p className="text-gray-600">Manage your properties, vehicles, and equipment with units</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Asset
                </button>
            </div>
            
            <div className="space-y-4">
                {assets.map((asset) => (
                    <div key={asset.id} className="bg-white rounded-lg shadow overflow-hidden">
                        {/* Asset Header */}
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => toggleAssetExpand(asset)}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        {expandedAsset === asset.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                    </button>
                                    <div className="flex items-center space-x-3">
                                        {getCategoryIcon(asset.category)}
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">{asset.name}</h3>
                                            <p className="text-sm text-gray-500">{asset.description}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-6">
                                    <div className="text-right">
                                        <p className="text-sm text-gray-500">Base Rate</p>
                                        <p className="text-lg font-bold text-indigo-600">KES {asset.base_rate?.toLocaleString()}</p>
                                    </div>
                                    <div className="border-l pl-4">
                                        <p className="text-sm text-gray-500">Billing Cycle</p>
                                        <p className="text-base font-semibold text-gray-900 capitalize">{asset.billing_cycle || 'Monthly'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-500">Units</p>
                                        <p className="text-lg font-semibold">{asset.unit_count || 0}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-500">Occupancy</p>
                                        <p className="text-lg font-semibold">
                                            {asset.unit_count > 0 ? Math.round((asset.rented_units || 0) / asset.unit_count * 100) : 0}%
                                        </p>
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                setSelectedAsset(asset.id);
                                                setShowUnitModal(true);
                                            }}
                                            className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                                            title="Add Unit"
                                        >
                                            <Layers className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => openEditModal(asset)}
                                            className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                                        >
                                            <Edit className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteAsset(asset.id, asset.name)}
                                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                            <Trash2 className="h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Units Section */}
                        {expandedAsset === asset.id && (
                            <div className="p-6 bg-gray-50">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-semibold text-gray-900">Units</h4>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                setSelectedAsset(asset.id);
                                                setShowBulkUnitModal(true);
                                            }}
                                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                        >
                                            Bulk Add Units
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedAsset(asset.id);
                                                setShowUnitModal(true);
                                            }}
                                            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                                        >
                                            Add Single Unit
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {units.map((unit) => (
                                        <div key={unit.id} className="bg-white rounded-lg p-4 shadow-sm border">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h5 className="font-medium text-gray-900">Unit {unit.unit_number}</h5>
                                                    <p className="text-sm text-gray-500 capitalize">{unit.status}</p>
                                                </div>
                                                <div className="flex space-x-1">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUnit(unit);
                                                            setSelectedAsset(asset.id);
                                                            setShowUnitModal(true);
                                                        }}
                                                        className="p-1 text-gray-400 hover:text-indigo-600"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUnit(unit.id)}
                                                        className="p-1 text-gray-400 hover:text-red-600"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-2 pt-2 border-t">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-sm text-gray-600">Rate:</span>
                                                    <span className="font-semibold">
                                                        {unit.rate_override ? (
                                                            <span>
                                                                KES {unit.rate_override.toLocaleString()}
                                                                <span className="text-xs text-gray-500 ml-1">
                                                                    (override)
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            `KES ${asset.base_rate?.toLocaleString()}`
                                                        )}
                                                    </span>
                                                </div>
                                                {unit.rate_override && (
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        Base: KES {asset.base_rate?.toLocaleString()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                {units.length === 0 && (
                                    <div className="text-center py-8 text-gray-500">
                                        No units added yet. Click "Add Unit" to create units for this asset.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {/* Create Asset Modal */}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Add New Asset" size="lg">
                <DynamicAssetForm 
                    assetType="custom" 
                    onSubmit={handleCreateAsset} 
                    initialData={{}}
                    onCancel={() => setShowModal(false)}
                />
            </Modal>

            {/* Edit Asset Modal */}
            <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedAsset(null); }} title="Edit Asset" size="lg">
                {selectedAsset && (
                    <DynamicAssetForm
                        assetType={selectedAsset.category}
                        onSubmit={handleUpdateAsset}
                        initialData={{
                            name: selectedAsset.name,
                            category: selectedAsset.category,
                            base_rate: selectedAsset.base_rate,
                            description: selectedAsset.description || '',
                            billing_cycle: selectedAsset.billing_cycle || 'monthly'
                        }}
                        isEdit={true}
                        onCancel={() => { setShowEditModal(false); setSelectedAsset(null); }}
                    />
                )}
            </Modal>

            {/* Add/Edit Unit Modal */}
            <Modal isOpen={showUnitModal} onClose={() => { setShowUnitModal(false); setSelectedUnit(null); setSelectedAsset(null); }} title={selectedUnit ? "Edit Unit" : "Add Unit"} size="md">
                <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const unitData = {
                        unit_number: formData.get('unit_number'),
                        name: formData.get('name') || null,
                        rate_override: formData.get('rate_override') ? parseFloat(formData.get('rate_override')) : null,
                        status: formData.get('status') || 'available'
                    };
                    if (selectedUnit) {
                        handleUpdateUnit(selectedUnit.id, unitData);
                    } else {
                        handleCreateUnit(unitData);
                    }
                }} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Unit Number *</label>
                        <input
                            type="text"
                            name="unit_number"
                            defaultValue={selectedUnit?.unit_number}
                            required
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="e.g., A101, Unit 1, Shop 1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Unit Name (Optional)</label>
                        <input
                            type="text"
                            name="name"
                            defaultValue={selectedUnit?.name}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="e.g., Premium Suite, Corner Office"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Custom Rate (Optional)
                            <span className="text-xs text-gray-500 ml-2">Leave empty to use base rate</span>
                        </label>
                        <div className="relative mt-1">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">KES</span>
                            <input
                                type="number"
                                name="rate_override"
                                defaultValue={selectedUnit?.rate_override}
                                className="pl-12 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Leave empty for base rate"
                                step="1000"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                            name="status"
                            defaultValue={selectedUnit?.status || 'available'}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="available">Available</option>
                            <option value="rented">Rented</option>
                            <option value="maintenance">Maintenance</option>
                        </select>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button type="button" onClick={() => { setShowUnitModal(false); setSelectedUnit(null); setSelectedAsset(null); }} className="px-4 py-2 border rounded-lg">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                            {selectedUnit ? 'Update' : 'Create'} Unit
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Bulk Add Units Modal */}
            <Modal isOpen={showBulkUnitModal} onClose={() => { if (!bulkProgress?.active) { setShowBulkUnitModal(false); setSelectedAsset(null); } }} title="Bulk Add Units" size="md">
                {/* Progress Overlay */}
                {bulkProgress && (
                    <div className="absolute inset-0 bg-white bg-opacity-95 z-10 flex items-center justify-center rounded-lg">
                        <div className="text-center p-6">
                            {bulkProgress.active ? (
                                <>
                                    <LoadingSpinner size="md" />
                                    <p className="mt-4 text-lg font-semibold text-gray-900">
                                        Creating units... {bulkProgress.current} of {bulkProgress.total}
                                    </p>
                                    <div className="mt-3 w-64 bg-gray-200 rounded-full h-3 mx-auto overflow-hidden">
                                        <div 
                                            className="bg-green-500 h-full rounded-full transition-all duration-300"
                                            style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                                        />
                                    </div>
                                    <p className="mt-2 text-sm text-gray-500">
                                        Please wait while units are being created
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                                        <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <p className="mt-4 text-lg font-semibold text-green-700">
                                        Complete!
                                    </p>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Successfully created {bulkProgress.total} unit(s)
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); handleBulkCreateUnits(); }} className="space-y-4">

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Unit Number Prefix</label>
                        <input
                            type="text"
                            value={bulkUnitData.prefix}
                            onChange={(e) => setBulkUnitData({ ...bulkUnitData, prefix: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            placeholder="e.g., A, Unit, Shop"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Starting Number</label>
                        <input
                            type="number"
                            value={bulkUnitData.startNumber}
                            onChange={(e) => setBulkUnitData({ ...bulkUnitData, startNumber: parseInt(e.target.value) })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Number of Units</label>
                        <input
                            type="number"
                            value={bulkUnitData.count}
                            onChange={(e) => setBulkUnitData({ ...bulkUnitData, count: parseInt(e.target.value) })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            min="1"
                            max="100"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Custom Rate for All Units (Optional)
                        </label>
                        <div className="relative mt-1">
                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">KES</span>
                            <input
                                type="number"
                                value={bulkUnitData.baseRate || ''}
                                onChange={(e) => setBulkUnitData({ ...bulkUnitData, baseRate: e.target.value ? parseFloat(e.target.value) : null })}
                                className="pl-12 block w-full rounded-md border-gray-300 shadow-sm"
                                placeholder="Leave empty to use base rate"
                            />
                        </div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-800">
                            Will create: {bulkUnitData.prefix}{bulkUnitData.startNumber} to {bulkUnitData.prefix}{bulkUnitData.startNumber + bulkUnitData.count - 1}
                            {bulkUnitData.baseRate && ` at KES ${bulkUnitData.baseRate.toLocaleString()} each`}
                        </p>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4">
                        <button type="button" onClick={() => setShowBulkUnitModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                            Create {bulkUnitData.count} Unit(s)
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default Assets;